import apiClient from '../client';
import { z } from 'zod';

// Public API endpoints (no authentication required)

// Public banner schema
const publicBannerSchema = z.object({
  id: z.number(),
  text: z.string(),
  isActive: z.boolean(),
  linkUrl: z.string().nullable().optional(),
});

const publicBannerResponseSchema = z.object({
  success: z.boolean(),
  data: publicBannerSchema.nullable(),
  message: z.string(),
});

// Trading rule schema (for public API)
const tradingRuleSchema = z.object({
  id: z.number(),
  title: z.string(),
  content: z.array(z.string()),
  order: z.number(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Public account banner schema (landing-page marquee)
const publicAccountBannerSchema = z.object({
  id: z.number(),
  text: z.string(),
  isActive: z.boolean(),
});

const publicAccountBannerResponseSchema = z.object({
  success: z.boolean(),
  data: publicAccountBannerSchema.nullable(),
  message: z.string(),
});

// Public extended banner schema (landing-page "extended through" section)
const publicExtendedBannerSchema = z.object({
  id: z.number(),
  highlightTitle: z.string(),
  titleSuffix: z.string(),
  bodyPart1: z.string(),
  bodyHighlight1: z.string(),
  bodyPart2: z.string(),
  bodyHighlight2: z.string(),
  bodyPart3: z.string(),
  ctaLabel: z.string(),
  ctaUrl: z.string(),
  isActive: z.boolean(),
});

const publicExtendedBannerResponseSchema = z.object({
  success: z.boolean(),
  data: publicExtendedBannerSchema.nullable(),
  message: z.string(),
});

export type PublicBanner = z.infer<typeof publicBannerSchema>;
export type PublicBannerResponse = z.infer<typeof publicBannerResponseSchema>;
export type TradingRule = z.infer<typeof tradingRuleSchema>;
export type PublicAccountBanner = z.infer<typeof publicAccountBannerSchema>;
export type PublicAccountBannerResponse = z.infer<typeof publicAccountBannerResponseSchema>;
export type PublicExtendedBanner = z.infer<typeof publicExtendedBannerSchema>;
export type PublicExtendedBannerResponse = z.infer<typeof publicExtendedBannerResponseSchema>;

export const publicApi = {
  // Get public trading rules (active only)
  getTradingRules: async () => {
    const response = await apiClient.get('/public/trading-rules');
    return response.data;
  },

  // Get active banner configuration (public)
  getBannerConfig: async () => {
    const response = await apiClient.get<PublicBannerResponse>('/public/banner-config');
    return response.data;
  },

  // Get active account banner (landing-page marquee) configuration (public)
  getAccountBannerConfig: async () => {
    const response = await apiClient.get<PublicAccountBannerResponse>('/public/account-banner-config');
    return response.data;
  },

  // Get active extended banner ("extended through" section) configuration (public)
  getExtendedBannerConfig: async () => {
    const response = await apiClient.get<PublicExtendedBannerResponse>('/public/extended-banner-config');
    return response.data;
  },
};